import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  costInputTypesApi,
  costTypeCatalogApi,
  unitsOfMeasureApi,
} from '../utils/api';
import Layout from '../components/Layout';
import SearchableSelect from '../components/SearchableSelect';
import { useAuth } from '../hooks/useAuth';

// PRD-022 WU-B — Cost Types & Units of Measure setup.
//
// Visual target: mockups/cost-types-units-setup-v1.html. Two iron pillars side
// by side: (1) the Cost Type catalog (what a cost IS), (2) the global Units-of-
// Measure registry (the unit). A unit is NEVER typed onto a cost — the cost
// inherits it from the registry. All editing is INLINE (house style) — no
// modals. Clicking Edit makes BOTH the unit (a registry dropdown) and the rate
// (an inline $ field) editable; the "/ unit" suffix updates live. Rates read
// "$0.45 / engagement" — never "$ per per engagement".

const CATEGORY_LABELS = {
  media_spend: 'Media spend',
  creator_fees: 'Creator fees',
  mmp_fees: 'MMP fees',
  brand_safety_fees: 'Brand safety',
  production: 'Production',
  agency_labor: 'Agency labor',
};

// PRD-027 WU-A — the rate effective TODAY is the cost_type_rates window covering
// CURRENT_DATE (today_rate_cents) — the single source of truth every surface
// resolves. Fall back to the negotiated override / catalog default only when no
// window covers today (legacy / not-yet-rated rows). null → no usable rate yet.
const effectiveRateCents = (t) =>
  t.today_rate_cents != null
    ? Number(t.today_rate_cents)
    : (t.negotiated_rate_override_cents != null
        ? Number(t.negotiated_rate_override_cents)
        : (t.catalog_default_unit_rate_cents != null ? Number(t.catalog_default_unit_rate_cents) : null));

// True when the displayed rate is a catalog/override fallback (no rate window
// covers today) — labelled distinctly so it never reads as a set rate.
const isFallbackRate = (t) => t.today_rate_cents == null;

// Render a rate as "$0.45", "$150", "$1,200" — 2 decimals under $100, thousands-
// separated whole dollars at/above $100 (matches the mockup examples). The
// "/ unit" suffix is composed separately so the stored unit stays a noun.
function fmtRate(cents) {
  const d = Number(cents) / 100;
  return d >= 100
    ? `$${d.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : `$${d.toFixed(2)}`;
}

// ---- PRD-027 WU-F — rate-window date/status helpers --------------------------
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Local "today" as YYYY-MM-DD (rate windows are DATE; compare as strings).
function todayStr() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function fmtDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-');
  return `${MONTHS[+m - 1]} ${+d}, ${y}`;
}
function fmtDateShort(s) {
  if (!s) return 'open-ended';
  const [, m, d] = s.split('-');
  return `${MONTHS[+m - 1]} ${+d}`;
}
function coversToday(r) {
  const T = todayStr();
  return (!r.valid_from || r.valid_from <= T) && (!r.valid_to || T < r.valid_to);
}
// 'now' (valid today) | 'up' (upcoming) | 'past' (ended).
function winStatus(r) {
  const T = todayStr();
  if (coversToday(r)) return 'now';
  if (r.valid_from && T < r.valid_from) return 'up';
  return 'past';
}

// Evergreen / Scheduled chip shown under the "Rate valid today" cell.
function ModeChip({ mode }) {
  const scheduled = mode === 'windowed';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mt-1 ${
      scheduled ? 'bg-primary-50 text-primary-700' : 'bg-slate-100 text-slate-600'
    }`}>
      <span className={`w-[5px] h-[5px] rounded-full ${scheduled ? 'bg-primary-500' : 'bg-slate-400'}`} />
      {scheduled ? 'Scheduled' : 'Evergreen'}
    </span>
  );
}

function CategoryBadge({ category }) {
  return (
    <span className="inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 text-slate-600">
      {CATEGORY_LABELS[category] || category}
    </span>
  );
}

function UnitPill({ children }) {
  return (
    <span className="inline-block whitespace-nowrap text-xs font-semibold text-gray-700 bg-slate-50 border border-gray-200 rounded-md px-2 py-0.5">
      {children}
    </span>
  );
}

export default function CostInputTypes() {
  const { workspace } = useAuth();
  const location = useLocation();
  // PRD-027 WU-E — Units of Measure is the same page deep-linked to the 'uom'
  // tab via the /units-of-measure route (its single canonical home).
  const [tab, setTab] = useState(location.pathname === '/units-of-measure' ? 'uom' : 'cost'); // 'cost' | 'uom'

  const [types, setTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  // PRD-027 WU-F — rate-schedule panel (parent Cost Type → child rate windows).
  // Rates are managed in an expandable per-row panel; the unit stays editable
  // there too (RJ: maximum flexibility/editability). One row open at a time.
  const [openRow, setOpenRow] = useState(null);
  const [ratesByType, setRatesByType] = useState({}); // id -> rate windows[]
  const [panelBusy, setPanelBusy] = useState(false);
  const [panelError, setPanelError] = useState('');
  const [pendingConvert, setPendingConvert] = useState(null); // id awaiting evergreen-confirm
  // Add-window form (one open at a time).
  const [addOpenId, setAddOpenId] = useState(null);
  const [addRate, setAddRate] = useState('');
  const [addFrom, setAddFrom] = useState('');
  const [addTo, setAddTo] = useState('');
  // Inline rate-window correction.
  const [editWinId, setEditWinId] = useState(null);
  const [editWinRate, setEditWinRate] = useState('');
  const [editWinTo, setEditWinTo] = useState('');
  const [editWinFrom, setEditWinFrom] = useState('');
  // Inline unit change (within the panel header).
  const [unitEditId, setUnitEditId] = useState(null);

  // Inline add-cost-type composer. Two paths through the same PRD-023 gateway:
  // 'catalog' (platform selection) | 'tenant' (structured authoring — PRD-026 F4).
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState('catalog');
  const [composerCatalogId, setComposerCatalogId] = useState('');
  const [composerUnitId, setComposerUnitId] = useState('');
  const [composerRate, setComposerRate] = useState('');
  const [composerError, setComposerError] = useState('');
  // Tenant-authoring fields (name + category enum + registry unit + rate).
  const [tenantName, setTenantName] = useState('');
  const [tenantCategory, setTenantCategory] = useState('');
  // Near-match dedup (PRD-023 FR-5), surfaced inline before the create commits.
  const [tenantMatches, setTenantMatches] = useState(null); // null = not checked yet
  const [tenantBusy, setTenantBusy] = useState(false);

  // Units tab: inline rename + add.
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [uomComposerOpen, setUomComposerOpen] = useState(false);
  const [uomName, setUomName] = useState('');
  const [uomCost, setUomCost] = useState(true);
  const [uomSell, setUomSell] = useState(false);
  const [uomError, setUomError] = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const [t, u, c] = await Promise.all([
        costInputTypesApi.list({ includeInactive: true }),
        unitsOfMeasureApi.list({ includeInactive: true }),
        costTypeCatalogApi.list({ active_only: true }),
      ]);
      setTypes(t.data.costInputTypes || []);
      setUnits(u.data.unitsOfMeasure || []);
      setCatalog(c.data.catalog || []);
    } catch (err) {
      console.error('Failed to load cost types / units:', err);
    } finally {
      setLoading(false);
    }
  }

  const unitName = (id) => units.find((u) => u.id === id)?.name || '';
  const activeTypes = types.filter((t) => t.is_active);
  const inactiveTypes = types.filter((t) => !t.is_active);

  // ---- PRD-027 WU-F — rate-schedule panel (parent/child rate windows) -------
  async function loadRates(id) {
    try {
      const r = await costInputTypesApi.listRates(id);
      setRatesByType((m) => ({ ...m, [id]: r.data.rates || [] }));
    } catch (err) {
      setPanelError(err.response?.data?.error || 'Failed to load rate schedule.');
    }
  }

  function toggleRow(id) {
    setPanelError(''); setAddOpenId(null); setEditWinId(null); setUnitEditId(null);
    if (openRow === id) { setOpenRow(null); return; }
    setOpenRow(id);
    if (!ratesByType[id]) loadRates(id);
  }

  // evergreen ↔ windowed. Dropping a multi-window schedule for evergreen warns
  // first (it removes the schedule). Other transitions apply immediately.
  async function changeMode(t, mode) {
    if (t.rate_mode === mode) return;
    const rates = ratesByType[t.id] || [];
    if (mode === 'evergreen' && rates.length > 1) { setPendingConvert(t.id); return; }
    await applyMode(t.id, mode);
  }
  async function applyMode(id, mode) {
    setPanelBusy(true); setPanelError('');
    try {
      await costInputTypesApi.setRateMode(id, { mode });
      await Promise.all([loadRates(id), loadAll()]);
    } catch (err) {
      setPanelError(err.response?.data?.error || 'Failed to change rate mode.');
    } finally { setPanelBusy(false); setPendingConvert(null); }
  }

  function openAdd(id) {
    setAddOpenId(id); setEditWinId(null);
    setAddRate(''); setAddFrom(''); setAddTo(''); setPanelError('');
  }
  async function saveAddWindow(id) {
    const dollars = parseFloat(addRate);
    if (addRate === '' || Number.isNaN(dollars) || dollars < 0) { setPanelError('Enter a rate of $0.00 or more.'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(addFrom)) { setPanelError('Enter a "valid from" date (YYYY-MM-DD).'); return; }
    if (addTo && !/^\d{4}-\d{2}-\d{2}$/.test(addTo)) { setPanelError('"Valid to" must be YYYY-MM-DD, or blank for open-ended.'); return; }
    if (addTo && addTo <= addFrom) { setPanelError('"Valid to" must be after "valid from".'); return; }
    setPanelBusy(true); setPanelError('');
    try {
      await costInputTypesApi.addRate(id, { rate_cents: Math.round(dollars * 100), valid_from: addFrom, valid_to: addTo || null });
      setAddOpenId(null);
      await Promise.all([loadRates(id), loadAll()]);
    } catch (err) {
      setPanelError(err.response?.data?.error || 'Failed to add rate window.');
    } finally { setPanelBusy(false); }
  }

  function startEditWin(rate) { setEditWinId(rate.id); setEditWinRate((Number(rate.rate_cents) / 100).toString()); setEditWinTo(rate.valid_to || ''); setEditWinFrom(rate.valid_from || ''); setPanelError(''); }
  async function saveEditWin(id, rateId) {
    const dollars = parseFloat(editWinRate);
    if (editWinRate === '' || Number.isNaN(dollars) || dollars < 0) { setPanelError('Enter a rate of $0.00 or more.'); return; }
    const to = editWinTo.trim();
    if (to && !/^\d{4}-\d{2}-\d{2}$/.test(to)) { setPanelError('End date must be YYYY-MM-DD, or blank for open-ended.'); return; }
    if (to && editWinFrom && to <= editWinFrom) { setPanelError('"Valid to" must be after the window’s "valid from".'); return; }
    setPanelBusy(true); setPanelError('');
    try {
      // Send valid_to too so an open-ended window can be capped (blank = open-ended).
      await costInputTypesApi.editRate(id, rateId, { rate_cents: Math.round(dollars * 100), valid_to: to || null });
      setEditWinId(null);
      await Promise.all([loadRates(id), loadAll()]);
    } catch (err) {
      setPanelError(err.response?.data?.error || 'Failed to correct the rate.');
    } finally { setPanelBusy(false); }
  }
  async function removeWindow(id, rateId) {
    if (!window.confirm('Remove this upcoming rate window?')) return;
    setPanelBusy(true); setPanelError('');
    try {
      await costInputTypesApi.removeRate(id, rateId);
      await Promise.all([loadRates(id), loadAll()]);
    } catch (err) {
      setPanelError(err.response?.data?.error || 'Failed to remove the rate window.');
    } finally { setPanelBusy(false); }
  }
  async function saveUnitChange(id, newUnitId) {
    setUnitEditId(null);
    setPanelBusy(true); setPanelError('');
    try {
      await costInputTypesApi.update(id, { unitOfMeasureId: newUnitId });
      await loadAll();
    } catch (err) {
      setPanelError(err.response?.data?.error || 'Failed to change the unit.');
    } finally { setPanelBusy(false); }
  }

  // ---- Add cost type (inline composer) ------------------------------------
  function openComposer() {
    setComposerOpen(true);
    setComposerMode('catalog');
    setComposerCatalogId('');
    setComposerUnitId('');
    setComposerRate('');
    setComposerError('');
    setTenantName('');
    setTenantCategory('');
    setTenantMatches(null);
  }

  function switchComposerMode(mode) {
    setComposerMode(mode);
    setComposerError('');
    setTenantMatches(null);
  }

  function onPickCatalog(catalogId) {
    setComposerCatalogId(catalogId);
    const entry = catalog.find((e) => e.id === catalogId);
    if (entry) {
      // Unit defaults from the catalog suggestion but stays editable from the
      // registry. USD-spent pass-through media spend pre-fills a 1.00 rate.
      const u = units.find((x) => x.name.toLowerCase() === String(entry.default_unit_of_measure).toLowerCase());
      setComposerUnitId(u ? u.id : '');
      if (entry.default_unit_of_measure === 'USD spent' && composerRate === '') setComposerRate('1.00');
    }
  }

  async function saveComposer() {
    if (!composerCatalogId) { setComposerError('Pick a cost type from the catalog.'); return; }
    if (!composerUnitId) { setComposerError('Pick a unit from the registry.'); return; }
    const dollars = parseFloat(composerRate);
    if (composerRate === '' || Number.isNaN(dollars) || dollars < 0) {
      setComposerError("Enter a rate — it can't be blank, that's what makes the cost calculable.");
      return;
    }
    try {
      await costInputTypesApi.create({
        catalog_entry_id: composerCatalogId,
        unit_of_measure_id: composerUnitId,
        negotiated_rate_override_cents: Math.round(dollars * 100),
      });
      setComposerOpen(false);
      await loadAll();
    } catch (err) {
      setComposerError(err.response?.data?.error || 'Failed to add cost type.');
    }
  }

  // PRD-026 F4 — tenant authoring through the PRD-023 gateway. The gateway owns
  // idempotency, audit, exact-dup 409s and the registry-unit guard; the UI only
  // surfaces its near-match dedup inline before committing.
  async function saveTenantComposer() {
    const name = tenantName.trim();
    if (!name) { setComposerError('Name the Cost Type.'); return; }
    if (!tenantCategory) { setComposerError('Pick a category.'); return; }
    if (!composerUnitId) { setComposerError('Pick a unit from the registry.'); return; }
    const dollars = parseFloat(composerRate);
    if (composerRate === '' || Number.isNaN(dollars) || dollars < 0) {
      setComposerError("Enter a rate — it can't be blank, that's what makes the cost calculable.");
      return;
    }
    setComposerError('');
    setTenantBusy(true);
    try {
      // First pass: surface near matches inline and pause. A second submit
      // ("Create anyway") proceeds — the gateway still hard-blocks exact dups.
      if (tenantMatches == null) {
        const m = await costInputTypesApi.match(name);
        const matches = m.data.matches || [];
        if (matches.length > 0) {
          setTenantMatches(matches);
          return;
        }
        setTenantMatches([]);
      }
      await costInputTypesApi.create({
        name,
        category: tenantCategory,
        unit_of_measure_id: composerUnitId,
        cost_per_unit_cents: Math.round(dollars * 100),
      });
      setComposerOpen(false);
      await loadAll();
    } catch (err) {
      const data = err.response?.data;
      if (data?.error_code === 'duplicate_cost_type') {
        setComposerError(data.existing
          ? `"${data.existing.name}" already exists in your workspace — use that one.`
          : data.error);
      } else {
        setComposerError(data?.error || 'Failed to create cost type.');
      }
    } finally {
      setTenantBusy(false);
    }
  }

  async function archive(id) {
    try { await costInputTypesApi.delete(id); await loadAll(); }
    catch (err) { console.error('archive failed', err); }
  }
  async function reactivate(id) {
    try { await costInputTypesApi.update(id, { isActive: true }); await loadAll(); }
    catch (err) { console.error('reactivate failed', err); }
  }

  // ---- Units tab ----------------------------------------------------------
  async function saveRename(u) {
    const name = renameValue.trim();
    setRenamingId(null);
    if (!name || name === u.name) return;
    try { await unitsOfMeasureApi.update(u.id, { name }); await loadAll(); }
    catch (err) { console.error('rename failed', err); }
  }

  async function saveUom() {
    const name = uomName.trim();
    if (!name) { setUomError('Name the unit (a noun, e.g. "lead").'); return; }
    const contexts = [...(uomCost ? ['cost'] : []), ...(uomSell ? ['sell'] : [])];
    if (contexts.length === 0) { setUomError('Pick at least one of cost / sell.'); return; }
    try {
      await unitsOfMeasureApi.create({ name, contexts });
      setUomComposerOpen(false); setUomName(''); setUomCost(true); setUomSell(false); setUomError('');
      await loadAll();
    } catch (err) {
      setUomError(err.response?.data?.error || 'Failed to add unit.');
    }
  }

  const sortedUnits = useMemo(
    () => [...units].sort((a, b) => a.name.localeCompare(b.name)),
    [units]
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900">Cost Types &amp; Units</h1>
        <p className="text-sm text-gray-600 mb-5">{workspace?.name || 'Your workspace'} · Admin Center</p>

        {/* Two iron pillars */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="flex-1 min-w-[260px] bg-white border border-gray-200 border-l-[3px] border-l-primary-600 rounded-lg px-3.5 py-3">
            <b className="text-[12.5px] text-gray-900">Cost Type catalog</b>
            <p className="mt-0.5 text-xs text-gray-600">What a cost <i>is</i> — platform-curated (Meta Ad Spend, AppsFlyer MMP Fee…). You set your negotiated rate; the catalog mapping is locked.</p>
          </div>
          <div className="flex-1 min-w-[260px] bg-white border border-gray-200 border-l-[3px] border-l-primary-600 rounded-lg px-3.5 py-3">
            <b className="text-[12.5px] text-gray-900">Units of Measure — global registry</b>
            <p className="mt-0.5 text-xs text-gray-600">The unit a cost or sell item is measured in. Defined once, here. A unit is never typed onto a cost — the cost <i>inherits</i> it.</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {[['cost', 'Cost Types'], ['uom', 'Units of Measure']].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`px-4 py-2.5 text-[13.5px] font-semibold -mb-px border-b-2 transition-colors ${tab === key ? 'text-primary-600 border-primary-600' : 'text-gray-600 border-transparent hover:text-gray-700'}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary-600" />
          </div>
        ) : tab === 'cost' ? (
          renderCostTypesPanel()
        ) : (
          renderUnitsPanel()
        )}
      </div>
    </Layout>
  );

  // ===== Cost Types panel ====================================================
  // Rendered as a function call (not <Component/>) so inline edit inputs keep
  // focus across re-renders.
  function renderCostTypesPanel() {
    return (
      <>
        <div className="bg-white border border-gray-200 border-t-0 rounded-b-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Cost Type</th>
                <th className="text-left text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Category</th>
                <th className="text-left text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Unit</th>
                <th className="text-right text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Rate valid today</th>
                <th className="text-right text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeTypes.length === 0 && !composerOpen && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-600">No cost types yet. Add one from the catalog below.</td></tr>
              )}
              {activeTypes.map((t) => {
                const eff = effectiveRateCents(t);
                const isOpen = openRow === t.id;
                return (
                  <React.Fragment key={t.id}>
                    <tr className={`border-b border-gray-100 align-middle cursor-pointer hover:bg-gray-50/60 ${isOpen ? 'bg-gray-50/60' : ''}`} onClick={() => toggleRow(t.id)}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900 text-[13.5px] flex items-center gap-1.5">
                          <span className={`text-gray-600 text-[10px] inline-block transition-transform ${isOpen ? 'rotate-90' : ''}`}>▶</span>
                          {t.name}
                        </div>
                        {t.catalog_provider && <div className="text-[11px] text-gray-600 ml-3.5">{t.catalog_provider}</div>}
                      </td>
                      {/* effective_category: catalog category for platform rows,
                          authored category for tenant rows (PRD-026 F4). */}
                      <td className="px-4 py-3"><CategoryBadge category={t.effective_category || t.catalog_category || t.category} /></td>
                      <td className="px-4 py-3"><UnitPill>{t.unit_name || t.unit_of_measure}</UnitPill></td>
                      {/* Rate valid today + mode chip (PRD-027 WU-A/WU-F). */}
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {eff == null ? (
                          <span className="text-xs font-medium text-amber-600">Rate not set</span>
                        ) : (
                          <div className="inline-flex flex-col items-end">
                            <span className="inline-flex items-center tabular-nums font-semibold text-gray-900 text-[13.5px]">
                              {fmtRate(eff)}<span className="text-gray-600 font-normal ml-1">/ {t.unit_name || t.unit_of_measure}</span>
                            </span>
                            {isFallbackRate(t)
                              ? <span className="text-[10.5px] text-amber-600">catalog default — no rate set</span>
                              : <ModeChip mode={t.rate_mode} />}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button onClick={(e) => { e.stopPropagation(); toggleRow(t.id); }} className="text-xs font-semibold text-primary-600 hover:text-primary-700">{isOpen ? 'Close' : 'Manage'}</button>
                        <button onClick={(e) => { e.stopPropagation(); archive(t.id); }} className="text-xs text-gray-600 hover:text-red-600 ml-3">Archive</button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-[#fbfcfe] border-b border-gray-100">
                        <td colSpan={5} className="px-4 pb-4 pt-0">
                          {renderSchedulePanel(t)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Inline composer (no modal) — catalog selection or tenant
                  authoring, both through the PRD-023 gateway (PRD-026 F4). */}
              {composerOpen && (
                <tr className="bg-[#fcfdff] border-b border-gray-200">
                  <td colSpan={5} className="px-4 py-4">
                    {composerMode === 'catalog' ? (
                      <>
                        <div className="flex flex-wrap gap-3 items-end">
                          <div className="flex flex-col gap-1 min-w-[260px]">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Cost Type (catalog)</label>
                            <SearchableSelect
                              options={catalog.map((e) => ({ id: e.id, name: e.name, category: CATEGORY_LABELS[e.category] || e.category }))}
                              value={composerCatalogId || null}
                              onChange={(id) => onPickCatalog(id || '')}
                              placeholder="Search the catalog…"
                              groupBy="category"
                            />
                          </div>
                          <div className="flex flex-col gap-1 min-w-[180px]">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Unit (from registry · editable)</label>
                            <SearchableSelect
                              options={sortedUnits}
                              value={composerUnitId || null}
                              onChange={(id) => setComposerUnitId(id || '')}
                              placeholder="Select a unit…"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Your rate</label>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="text-gray-600">$</span>
                              <input type="number" step="any" min="0" value={composerRate}
                                onChange={(e) => setComposerRate(e.target.value)} placeholder="0.00"
                                className="w-24 border border-gray-300 rounded-md px-2 py-2 text-sm tabular-nums" />
                              <span className="text-gray-600 text-xs">/ {unitName(composerUnitId) || 'unit'}</span>
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={saveComposer} className="btn-primary py-2">Add Cost Type</button>
                            <button onClick={() => setComposerOpen(false)} className="btn-secondary py-2">Cancel</button>
                          </div>
                        </div>
                        <p className="text-[11.5px] text-gray-600 mt-2">
                          The unit comes from the global registry — pick or change it freely, but you can't free-type a unit here. Need one that doesn't exist? Add it on the Units of Measure tab.
                          {' '}Can't find the cost type itself?{' '}
                          <button onClick={() => switchComposerMode('tenant')} className="text-primary-600 font-semibold hover:text-primary-700">Create your own</button>.
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-3 items-end">
                          <div className="flex flex-col gap-1 min-w-[220px]">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Name</label>
                            <input value={tenantName}
                              onChange={(e) => { setTenantName(e.target.value); setTenantMatches(null); }}
                              placeholder="e.g. Podcast host-read sponsorship"
                              className="border border-gray-300 rounded-md px-2.5 py-2 text-sm" />
                          </div>
                          <div className="flex flex-col gap-1 min-w-[170px]">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Category</label>
                            <SearchableSelect
                              options={Object.entries(CATEGORY_LABELS).map(([id, name]) => ({ id, name }))}
                              value={tenantCategory || null}
                              onChange={(id) => setTenantCategory(id || '')}
                              placeholder="Select a category…"
                            />
                          </div>
                          <div className="flex flex-col gap-1 min-w-[180px]">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Unit (from registry)</label>
                            <SearchableSelect
                              options={sortedUnits}
                              value={composerUnitId || null}
                              onChange={(id) => setComposerUnitId(id || '')}
                              placeholder="Select a unit…"
                            />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Your rate</label>
                            <span className="inline-flex items-center gap-1.5">
                              <span className="text-gray-600">$</span>
                              <input type="number" step="any" min="0" value={composerRate}
                                onChange={(e) => setComposerRate(e.target.value)} placeholder="0.00"
                                className="w-24 border border-gray-300 rounded-md px-2 py-2 text-sm tabular-nums" />
                              <span className="text-gray-600 text-xs">/ {unitName(composerUnitId) || 'unit'}</span>
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={saveTenantComposer} disabled={tenantBusy} className="btn-primary py-2">
                              {tenantMatches && tenantMatches.length > 0 ? 'Create anyway' : 'Create Cost Type'}
                            </button>
                            <button onClick={() => setComposerOpen(false)} className="btn-secondary py-2">Cancel</button>
                          </div>
                        </div>
                        {/* Near-match dedup, surfaced inline (PRD-023 FR-5) */}
                        {tenantMatches && tenantMatches.length > 0 && (
                          <div className="mt-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-xs text-amber-800">
                            <b>Similar Cost Types already exist in your list:</b>{' '}
                            {tenantMatches.map((m) => m.name).join(' · ')}.
                            {' '}If one of these is the same thing, use it instead — duplicate names fragment your cost data. Otherwise, "Create anyway".
                          </div>
                        )}
                        <p className="text-[11.5px] text-gray-600 mt-2">
                          Your own Cost Types live alongside catalog ones and behave identically. The unit comes from the global registry — never free-typed; need a new unit? Add it on the Units of Measure tab.
                          {' '}<button onClick={() => switchComposerMode('catalog')} className="text-primary-600 font-semibold hover:text-primary-700">← Back to the catalog</button>
                        </p>
                      </>
                    )}
                    {composerError && <p className="text-xs text-red-600 mt-1">{composerError}</p>}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="px-4 py-3">
            {!composerOpen && (
              <button onClick={openComposer}
                className="bg-primary-50 text-primary-600 border border-dashed border-primary-200 rounded-lg px-3.5 py-2.5 font-bold text-[13px] hover:bg-primary-100">
                + Add Cost Type
              </button>
            )}
          </div>
        </div>

        <p className="text-[11.5px] text-gray-600 mt-2">
          Rate and unit edits apply to new proposal estimates and new consumption only; signed Agreements keep their snapshotted rate and unit.
        </p>

        {inactiveTypes.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Archived</h3>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden opacity-60">
              <table className="w-full">
                <tbody>
                  {inactiveTypes.map((t) => (
                    <tr key={t.id} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2.5 text-sm text-gray-600">{t.name}</td>
                      <td className="px-4 py-2.5"><CategoryBadge category={t.effective_category || t.catalog_category || t.category} /></td>
                      <td className="px-4 py-2.5 text-sm text-gray-600">{t.unit_name || t.unit_of_measure}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button onClick={() => reactivate(t.id)} className="text-xs text-primary-600 hover:text-primary-700">Reactivate</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* PRD-027 WU-F — convert-to-evergreen confirm (drops a multi-window schedule). */}
        {pendingConvert && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/45" onClick={() => setPendingConvert(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-base font-semibold text-gray-900 mb-1.5">Switch to a single evergreen rate?</h3>
              <p className="text-[13px] text-gray-600 mb-4">
                This Cost Type has scheduled rate windows. Switching to Evergreen keeps only the rate valid today and <b>removes its schedule</b> (past windows stay on record for signed Agreements). A Cost Type can’t hold both evergreen and scheduled rates.
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPendingConvert(null)} className="btn-secondary">Cancel</button>
                <button onClick={() => applyMode(pendingConvert, 'evergreen')} disabled={panelBusy} className="btn-primary">Switch to evergreen</button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // ===== PRD-027 WU-F — per-Cost-Type rate-schedule panel ====================
  function renderSchedulePanel(t) {
    const rates = ratesByType[t.id];
    const eff = effectiveRateCents(t);
    const sorted = rates ? [...rates].sort((a, b) => (a.valid_from || '').localeCompare(b.valid_from || '')) : [];
    return (
      <div className="border border-gray-200 rounded-xl bg-white overflow-hidden mt-0.5">
        {/* header: mode toggle + editable unit */}
        <div className="flex justify-between items-center gap-3 px-4 py-3 border-b border-gray-200 bg-[#fafbfc]">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-extrabold uppercase tracking-wide text-gray-600">Rate schedule</span>
            <span className="text-[11px] text-gray-600">Unit:</span>
            {unitEditId === t.id ? (
              <select autoFocus defaultValue={t.unit_of_measure_id || ''}
                onChange={(e) => saveUnitChange(t.id, e.target.value)}
                onBlur={() => setUnitEditId(null)}
                className="border border-primary-500 rounded-md px-1.5 py-1 text-xs bg-white">
                {sortedUnits.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            ) : (
              <button onClick={() => setUnitEditId(t.id)} className="inline-flex items-center gap-1 group">
                <UnitPill>{t.unit_name || t.unit_of_measure}</UnitPill>
                <span className="text-[11px] text-primary-600 opacity-0 group-hover:opacity-100">change</span>
              </button>
            )}
          </div>
          <div className="inline-flex bg-gray-100 rounded-lg p-0.5">
            {[['evergreen', 'Evergreen'], ['windowed', 'Scheduled']].map(([m, label]) => (
              <button key={m} disabled={panelBusy} onClick={() => changeMode(t, m)}
                className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${t.rate_mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-700'}`}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* rate valid today (the single source of truth) */}
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border-b border-emerald-100">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">Rate valid today · {fmtDateShort(todayStr())}</div>
            <div className="text-[17px] font-extrabold tabular-nums text-emerald-800">
              {eff == null ? '—' : fmtRate(eff)} <span className="text-xs font-semibold text-emerald-700">/ {t.unit_name || t.unit_of_measure}</span>
            </div>
          </div>
          <div className="ml-auto text-xs font-semibold text-emerald-700">→ flows to every proposal &amp; agreement</div>
        </div>

        {/* body: evergreen single rate, or the windowed schedule */}
        {!rates ? (
          <div className="flex items-center justify-center py-6"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600" /></div>
        ) : t.rate_mode !== 'windowed' ? (
          <>
            <div className="flex items-center gap-3.5 px-4 py-3.5">
              {editWinId === (sorted[0] && sorted[0].id) ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-gray-600">$</span>
                  <input type="number" step="any" min="0" autoFocus value={editWinRate}
                    onChange={(e) => setEditWinRate(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEditWin(t.id, sorted[0].id); if (e.key === 'Escape') setEditWinId(null); }}
                    className="w-24 text-right border border-primary-500 rounded-md px-1.5 py-1 text-sm tabular-nums" />
                  <button onClick={() => saveEditWin(t.id, sorted[0].id)} className="text-xs font-bold text-primary-600 ml-1">Save</button>
                  <button onClick={() => setEditWinId(null)} className="text-xs text-gray-600 ml-2">Cancel</button>
                </span>
              ) : (
                <>
                  <span className="text-xl font-extrabold tabular-nums text-gray-900">{sorted[0] ? fmtRate(Number(sorted[0].rate_cents)) : '—'}<span className="text-[13px] font-medium text-gray-600"> / {t.unit_name || t.unit_of_measure}</span></span>
                  <span className="text-[11.5px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">Always valid · no end date</span>
                  <div className="flex-1" />
                  {sorted[0] && <button onClick={() => startEditWin(sorted[0])} className="text-[12.5px] font-bold text-primary-600 hover:text-primary-700">Edit rate</button>}
                </>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 bg-[#fafbfc]">
              <span className="text-[11.5px] text-gray-600">Evergreen — one rate, no schedule. Switch to <b>Scheduled</b> to set dated windows for a rate that changes over time.</span>
            </div>
          </>
        ) : (
          <>
            <div className="px-1.5 py-1.5">
              {sorted.map((r) => {
                const st = winStatus(r);
                const stLbl = st === 'now' ? 'Valid now' : st === 'up' ? 'Upcoming' : 'Ended';
                const stCls = st === 'now' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : st === 'up' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200';
                return (
                  <div key={r.id} className={`grid grid-cols-[130px_1fr_auto] items-center gap-3 px-3 py-2.5 rounded-lg ${st === 'past' ? 'opacity-60' : ''}`}>
                    {editWinId === r.id ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-gray-600 text-sm">$</span>
                        <input type="number" step="any" min="0" autoFocus value={editWinRate}
                          onChange={(e) => setEditWinRate(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEditWin(t.id, r.id); if (e.key === 'Escape') setEditWinId(null); }}
                          className="w-20 text-right border border-primary-500 rounded-md px-1.5 py-0.5 text-sm tabular-nums" />
                      </span>
                    ) : (
                      <span className="font-bold tabular-nums text-sm text-gray-900">{fmtRate(Number(r.rate_cents))}<span className="text-[12px] font-medium text-gray-600"> / {t.unit_name || t.unit_of_measure}</span></span>
                    )}
                    <span className="text-[12.5px] text-gray-600 tabular-nums">
                      {fmtDateShort(r.valid_from)}<span className="text-gray-600 mx-1.5">→</span>
                      {editWinId === r.id ? (
                        <input type="date" value={editWinTo} min={r.valid_from || undefined}
                          onChange={(e) => setEditWinTo(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEditWin(t.id, r.id); if (e.key === 'Escape') setEditWinId(null); }}
                          title="Blank = open-ended"
                          className="w-36 border border-primary-500 rounded-md px-1.5 py-0.5 text-[12.5px] tabular-nums" />
                      ) : (r.valid_to ? fmtDateShort(r.valid_to) : 'open-ended')}
                      <span className={`text-[10px] font-extrabold uppercase tracking-wide px-2 py-0.5 rounded-full border ml-2 ${stCls}`}>{stLbl}</span>
                    </span>
                    <span className="text-right whitespace-nowrap">
                      {editWinId === r.id ? (
                        <>
                          <button onClick={() => saveEditWin(t.id, r.id)} className="text-[12px] font-bold text-primary-600">Save</button>
                          <button onClick={() => setEditWinId(null)} className="text-[12px] text-gray-600 ml-2">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEditWin(r)} className="text-[12px] font-semibold text-primary-600 hover:text-primary-700">Edit</button>
                          {st === 'up' && <button onClick={() => removeWindow(t.id, r.id)} className="text-[12px] text-gray-600 hover:text-red-600 ml-2.5">Remove</button>}
                        </>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between items-center gap-3 px-4 py-2.5 border-t border-gray-200 bg-[#fafbfc]">
              <button onClick={() => openAdd(t.id)} className="inline-flex items-center gap-1.5 text-[12.5px] font-bold text-primary-600 hover:text-primary-700">＋ Add a future rate</button>
              <span className="text-[11.5px] text-gray-600 max-w-[520px] text-right">Exactly one rate is valid at any moment. Proposals freeze the rate valid <b>at signing</b>; actual costs use the rate valid on the <b>entry date</b>.</span>
            </div>
            {addOpenId === t.id && (
              <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 bg-primary-50 border-t border-primary-100">
                <label className="text-[11px] font-bold text-gray-600">New rate $</label>
                <input className="w-24 text-right border border-gray-300 rounded-md px-2 py-1 text-sm tabular-nums" value={addRate} onChange={(e) => setAddRate(e.target.value)} placeholder="0.06" autoFocus />
                <label className="text-[11px] font-bold text-gray-600">valid from</label>
                <input type="date" className="border border-gray-300 rounded-md px-2 py-1 text-sm tabular-nums" value={addFrom} onChange={(e) => setAddFrom(e.target.value)} />
                <label className="text-[11px] font-bold text-gray-600">valid to</label>
                <input type="date" className="border border-gray-300 rounded-md px-2 py-1 text-sm tabular-nums" value={addTo} min={addFrom || undefined} onChange={(e) => setAddTo(e.target.value)} title="Blank = open-ended" />
                <button onClick={() => saveAddWindow(t.id)} disabled={panelBusy} className="btn-primary py-1">Add window</button>
                <button onClick={() => setAddOpenId(null)} className="btn-secondary py-1">Cancel</button>
                <span className="text-[11px] text-gray-600">blank “valid to” = open-ended · closes the current open window at “valid from”</span>
              </div>
            )}
          </>
        )}
        {panelError && <div className="px-4 py-2 text-xs text-red-600 border-t border-gray-100">{panelError}</div>}
      </div>
    );
  }

  // ===== Units of Measure panel ==============================================
  function renderUnitsPanel() {
    return (
      <div className="bg-white border border-gray-200 border-t-0 rounded-b-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Unit</th>
              <th className="text-left text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Used for</th>
              <th className="text-right text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Referenced by</th>
              <th className="text-right text-[10px] font-extrabold uppercase tracking-wide text-gray-600 px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedUnits.map((u) => {
              const renaming = renamingId === u.id;
              const refs = [];
              if (u.cost_ref_count > 0) refs.push(`${u.cost_ref_count} cost type${u.cost_ref_count === 1 ? '' : 's'}`);
              if (u.sell_ref_count > 0) refs.push(`${u.sell_ref_count} sell unit${u.sell_ref_count === 1 ? '' : 's'}`);
              return (
                <tr key={u.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-4 py-3">
                    {renaming ? (
                      <input value={renameValue} autoFocus
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => saveRename(u)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(u); if (e.key === 'Escape') setRenamingId(null); }}
                        className="border border-primary-500 rounded-md px-2 py-1 text-sm w-36" />
                    ) : (
                      <UnitPill>{u.name}</UnitPill>
                    )}
                  </td>
                  <td className="px-4 py-3 space-x-1.5">
                    {(u.contexts || []).includes('cost') && <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-100 text-amber-800">cost</span>}
                    {(u.contexts || []).includes('sell') && <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-primary-100 text-primary-700">sell</span>}
                  </td>
                  <td className="px-4 py-3 text-right text-sm tabular-nums text-gray-600">
                    {refs.length ? refs.join(' · ') : <span className="text-gray-600">not used yet</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => { setRenamingId(u.id); setRenameValue(u.name); }}
                      className="text-xs font-semibold text-primary-600 hover:text-primary-700">Rename</button>
                  </td>
                </tr>
              );
            })}

            {uomComposerOpen && (
              <tr className="bg-[#fcfdff] border-b border-gray-200">
                <td colSpan={4} className="px-4 py-4">
                  <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Unit name (a noun)</label>
                      <input value={uomName} onChange={(e) => setUomName(e.target.value)}
                        placeholder="e.g. lead, app open, GRP"
                        className="border border-gray-300 rounded-md px-2.5 py-2 text-sm w-56" />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-extrabold uppercase tracking-wide text-gray-600">Used for</label>
                      <span className="inline-flex gap-3 items-center text-sm py-1.5">
                        <label className="inline-flex gap-1.5 items-center"><input type="checkbox" checked={uomCost} onChange={(e) => setUomCost(e.target.checked)} /> cost</label>
                        <label className="inline-flex gap-1.5 items-center"><input type="checkbox" checked={uomSell} onChange={(e) => setUomSell(e.target.checked)} /> sell</label>
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={saveUom} className="btn-primary py-2">Add unit</button>
                      <button onClick={() => { setUomComposerOpen(false); setUomError(''); }} className="btn-secondary py-2">Cancel</button>
                    </div>
                  </div>
                  <p className="text-[11.5px] text-gray-600 mt-2">
                    Name it as a noun ("install", "hour", "lead") — not "per install." The "per" is added wherever the unit is shown. This is the single place units are defined; cost types and sell configs both inherit from here.
                  </p>
                  {uomError && <p className="text-xs text-red-600 mt-1">{uomError}</p>}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="px-4 py-3">
          {!uomComposerOpen && (
            <button onClick={() => setUomComposerOpen(true)}
              className="bg-primary-50 text-primary-600 border border-dashed border-primary-200 rounded-lg px-3.5 py-2.5 font-bold text-[13px] hover:bg-primary-100">
              + Add unit
            </button>
          )}
        </div>
      </div>
    );
  }
}
